import React, {useCallback, useEffect, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {AgencyRepository} from '../../repositories/AgencyRepository.ts'
import {AuthenticationRepository} from '../../repositories/AuthenticationRepository.ts'
import {GetAgencyUsersUseCase} from '../../domain/useCases/agencies/GetAgencyUsersUseCase.ts'
import {SetAgencyUserDisabledUseCase} from '../../domain/useCases/agencies/SetAgencyUserDisabledUseCase.ts'
import {DeleteAgencyUserUseCase} from '../../domain/useCases/agencies/DeleteAgencyUserUseCase.ts'
import {AgencyUser, AgencyUserAuthMethod} from '../../domain/entities/AgencyUser.ts'
import {availableMethodsFor, CreateAgencyUserDialog} from '../dialogs/CreateAgencyUserDialog.tsx'
import {ConfirmDialog} from '../dialogs/ConfirmDialog.tsx'
import {Dialog} from '../Dialog.tsx'
import {Button} from '../Button.tsx'
import {initialsOf} from '../../utils/initials.ts'
import IconSpinner from '../../assets/icons/spinner.svg?react'
import IconTrash from '../../assets/icons/trash.svg?react'
import IconPlus from '../../assets/icons/plus.svg?react'

const METHOD_LABEL_KEY: Record<AgencyUserAuthMethod, string> = {
    password: 'agencyUsers.methodPassword',
    phone: 'agencyUsers.methodPhone',
    magicLink: 'agencyUsers.methodMagicLink',
}

const agencyRepo = new AgencyRepository(new AuthenticationRepository())
const getAgencyUsersUseCase = new GetAgencyUsersUseCase(agencyRepo)
const setDisabledUseCase = new SetAgencyUserDisabledUseCase(agencyRepo)
const deleteAgencyUserUseCase = new DeleteAgencyUserUseCase(agencyRepo)

interface AgencyUsersSectionProps {
    agencyId: string
}

// Lets a 51trips tourOperator user manage who can access this agency's own
// dashboard — the three creation flows (password / phone / magic link) live
// in CreateAgencyUserDialog; this component owns the list and its actions.
export const AgencyUsersSection: React.FC<AgencyUsersSectionProps> = ({agencyId}) => {
    const {t} = useTranslation('dashboard')
    const [users, setUsers] = useState<AgencyUser[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [togglingUid, setTogglingUid] = useState<string | null>(null)
    const [pendingDeleteUid, setPendingDeleteUid] = useState<string | null>(null)
    // Non-null while the dialog is adding a method to an existing user rather
    // than creating a brand new one — set right before opening it.
    const [linkingUser, setLinkingUser] = useState<AgencyUser | null>(null)

    const createDialog = useRef<React.ElementRef<typeof CreateAgencyUserDialog>>(null)
    const confirmDeleteDialog = useRef<React.ElementRef<typeof Dialog<boolean>>>(null)

    const loadUsers = useCallback(() => {
        if (!agencyId) return
        setIsLoading(true)
        getAgencyUsersUseCase.execute(agencyId)
            .then(setUsers)
            .catch((err) => console.error('Error loading agency users:', err))
            .finally(() => setIsLoading(false))
    }, [agencyId])

    useEffect(() => { loadUsers() }, [loadUsers])

    const toggleDisabled = (user: AgencyUser) => {
        setTogglingUid(user.uid)
        setDisabledUseCase.execute(agencyId, user.uid, !user.disabled)
            .then(() => setUsers((prev) => prev.map((u) => u.uid === user.uid ? {...u, disabled: !u.disabled} : u)))
            .catch((err) => console.error('Error updating agency user:', err))
            .finally(() => setTogglingUid(null))
    }

    const openAddUser = () => {
        setLinkingUser(null)
        createDialog.current?.open()
    }

    const openAddMethod = (user: AgencyUser) => {
        setLinkingUser(user)
        createDialog.current?.open()
    }

    const askDelete = (uid: string) => {
        setPendingDeleteUid(uid)
        confirmDeleteDialog.current?.open()
    }

    // ConfirmDialog closes itself right away when onConfirm returns truthy —
    // it has no notion of an in-flight async confirm — so the delete request
    // just runs in the background after that.
    const confirmDelete = () => {
        if (!pendingDeleteUid) return false
        const uid = pendingDeleteUid
        deleteAgencyUserUseCase.execute(agencyId, uid)
            .then(() => setUsers((prev) => prev.filter((u) => u.uid !== uid)))
            .catch((err) => console.error('Error deleting agency user:', err))
            .finally(() => setPendingDeleteUid(null))
        return true
    }

    return (
        <section className="bg-white border border-neutralBlack-200 rounded-2xl shadow-sm p-5 flex flex-col gap-4">
            <div className="flex items-center gap-3 flex-wrap">
                <div className="flex flex-col gap-0.5">
                    <span className="text-[11.5px] font-medium tracking-wide uppercase text-neutralBlack-500">{t('agencyUsers.sectionTitle')}</span>
                    <span className="text-xs text-neutralBlack-400">{t('agencyUsers.sectionHelp')}</span>
                </div>
                <span className="flex-1 min-w-[8px]"/>
                <Button variant="primary" size="small" className="max-h-[36px]" onClick={openAddUser}>
                    + {t('agencyUsers.addUser')}
                </Button>
            </div>

            {isLoading ? (
                <div className="flex justify-center py-6">
                    <IconSpinner className="size-6"/>
                </div>
            ) : users.length === 0 ? (
                <div className="text-center py-8 text-sm text-neutralBlack-500">
                    {t('agencyUsers.noUsersYet')}
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[640px] border-collapse text-[13.5px]">
                        <thead>
                            <tr>
                                <th className="text-left py-2.5 font-medium text-[11px] uppercase tracking-wide text-neutralBlack-500 border-b border-neutralBlack-100">
                                    {t('agencyUsers.columnUser')}
                                </th>
                                <th className="text-left py-2.5 px-3.5 font-medium text-[11px] uppercase tracking-wide text-neutralBlack-500 border-b border-neutralBlack-100">
                                    {t('agencyUsers.columnMethod')}
                                </th>
                                <th className="text-left py-2.5 px-3.5 font-medium text-[11px] uppercase tracking-wide text-neutralBlack-500 border-b border-neutralBlack-100">
                                    {t('agencyUsers.columnStatus')}
                                </th>
                                <th className="text-left py-2.5 px-3.5 font-medium text-[11px] uppercase tracking-wide text-neutralBlack-500 border-b border-neutralBlack-100">
                                    {t('agencyUsers.columnCreatedAt')}
                                </th>
                                <th className="w-[180px] border-b border-neutralBlack-100"/>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((user) => (
                                <tr key={user.uid} className="border-b border-neutralBlack-100 last:border-0 hover:bg-neutralBlack-50">
                                    <td className="py-3">
                                        <div className="flex items-center gap-2.5">
                                            <span className="w-8 h-8 flex-none rounded-full bg-neutralBlack-100 flex items-center justify-center text-[11px] font-semibold text-neutralBlack-700">
                                                {initialsOf(user.displayName ?? undefined)}
                                            </span>
                                            <span className="flex flex-col gap-0.5 min-w-0">
                                                <span className="text-neutralBlack-900 font-medium truncate">
                                                    {user.displayName || t('agencyUsers.noNameYet')}
                                                </span>
                                                <span className="text-xs text-neutralBlack-500 truncate">
                                                    {user.email ?? user.phoneNumber ?? '—'}
                                                </span>
                                            </span>
                                        </div>
                                    </td>
                                    <td className="py-3 px-3.5">
                                        <div className="flex flex-wrap gap-1.5">
                                            {user.authMethods.map((m) => (
                                                <span key={m} className="inline-flex items-center rounded-full bg-neutralBlack-100 px-2.5 py-1 text-xs text-neutralBlack-700 whitespace-nowrap">
                                                    {t(METHOD_LABEL_KEY[m])}
                                                </span>
                                            ))}
                                        </div>
                                    </td>
                                    <td className="py-3 px-3.5 whitespace-nowrap">
                                        <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs whitespace-nowrap ${
                                            user.disabled ? 'bg-neutralBlack-100 text-neutralBlack-600' : 'bg-success/15 text-neutralBlack-900'
                                        }`}>
                                            <span className={`w-[7px] h-[7px] rounded-full flex-none ${user.disabled ? 'bg-neutralBlack-400' : 'bg-success'}`}/>
                                            {user.disabled ? t('agencyUsers.statusDisabled') : t('agencyUsers.statusEnabled')}
                                        </span>
                                    </td>
                                    <td className="py-3 px-3.5 whitespace-nowrap text-neutralBlack-500">
                                        {formatCreatedAt(user.createdAt)}
                                    </td>
                                    <td className="py-3">
                                        <div className="flex items-center justify-end gap-1.5">
                                            {availableMethodsFor(user).length > 0 && (
                                                <button
                                                    type="button"
                                                    title={t('agencyUsers.addMethod')}
                                                    onClick={() => openAddMethod(user)}
                                                    className="w-[26px] h-[26px] flex-none flex items-center justify-center rounded-full text-neutralBlack-400 hover:bg-neutralBlack-100 hover:text-neutralBlack-900"
                                                >
                                                    <IconPlus className="size-3"/>
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                disabled={togglingUid === user.uid}
                                                onClick={() => toggleDisabled(user)}
                                                className="inline-flex items-center gap-1.5 h-[26px] px-2.5 rounded-full border border-neutralBlack-300 bg-white text-xs font-medium text-neutralBlack-900 hover:bg-neutralBlack-50 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                                            >
                                                {togglingUid === user.uid
                                                    ? <IconSpinner className="size-2.5"/>
                                                    : user.disabled ? t('agencyUsers.enableAccess') : t('agencyUsers.disableAccess')}
                                            </button>
                                            <button
                                                type="button"
                                                title={t('agencyUsers.deleteUser')}
                                                onClick={() => askDelete(user.uid)}
                                                className="w-[26px] h-[26px] flex-none flex items-center justify-center rounded-full text-neutralBlack-400 hover:bg-danger/10 hover:text-danger"
                                            >
                                                <IconTrash className="size-3"/>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <CreateAgencyUserDialog ref={createDialog} agencyId={agencyId} existingUser={linkingUser} onCreated={loadUsers}/>

            <ConfirmDialog
                ref={confirmDeleteDialog}
                onConfirm={confirmDelete}
                title={t('agencyUsers.deleteUserConfirmTitle')}
                message={t('agencyUsers.deleteUserConfirmMessage')}
                confirmLabel={t('agencyUsers.deleteUser')}
                cancelLabel={t('cancel')}
            />
        </section>
    )
}

function formatCreatedAt(timestamp: number | null): string {
    if (!timestamp) return '—'
    return new Intl.DateTimeFormat('es-ES', {day: '2-digit', month: '2-digit', year: 'numeric'}).format(new Date(timestamp))
}
